import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";

type FornoEventType =
  | "forno.created"
  | "forno.finalized"
  | "filaIntegracao.updated"
  | "producao.created"
  | "paozinhoPreparo.updated";

export const useFornoEvents = (
  onEvent: () => void,
  lojaId?: number | null,
  options?: {
    onAvailabilityChange?: (available: boolean) => void;
    enabled?: boolean;
  }
) => {
  const { token } = useAuth();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;
  const onAvailabilityChangeRef = useRef(options?.onAvailabilityChange);
  onAvailabilityChangeRef.current = options?.onAvailabilityChange;
  const enabledRef = useRef(options?.enabled !== false);
  enabledRef.current = options?.enabled !== false;

  const notifyAvailability = useCallback((available: boolean) => {
    onAvailabilityChangeRef.current?.(available);
  }, []);

  const connect = useCallback(() => {
    if (!token || !enabledRef.current) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";
    const params = new URLSearchParams();
    if (lojaId) {
      params.append("lojaId", lojaId.toString());
    }
    params.append("token", token);
    const url = `${apiUrl}/events/ranking/forno?${params.toString()}`;

    const eventSource = new EventSource(url, {
      withCredentials: false,
    });

    eventSource.onopen = () => {
      reconnectAttempts.current = 0;
      notifyAvailability(true);
    };

    eventSource.addEventListener("connected", () => {
      reconnectAttempts.current = 0;
      notifyAvailability(true);
      onEvent();
    });

    const eventTypes: FornoEventType[] = [
      "forno.created",
      "forno.finalized",
      "filaIntegracao.updated",
      "producao.created",
      "paozinhoPreparo.updated",
    ];

    eventTypes.forEach((eventType) => {
      eventSource.addEventListener(eventType, () => {
        onEvent();
      });
    });

    eventSource.onerror = (error) => {
      console.error("SSE error for forno:", error);
      eventSource.close();

      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current += 1;
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, reconnectDelay * reconnectAttempts.current);
      } else {
        notifyAvailability(false);
      }
    };

    eventSourceRef.current = eventSource;
  }, [token, lojaId, onEvent, notifyAvailability]);

  const enabled = options?.enabled !== false;

  useEffect(() => {
    if (!enabled) {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      return;
    }

    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [connect, enabled]);

  return { reconnect: connect };
};
